"use client";

import {
	type EmojiPickerListCategoryHeaderProps,
	type EmojiPickerListEmojiProps,
	type EmojiPickerListRowProps,
	EmojiPicker as EmojiPickerPrimitive,
	type EmojiPickerRootProps,
} from "frimousse";
import type { ComponentProps, CSSProperties } from "react";
import { useStore } from "zustand";
import { uiStore } from "@/lib/stores/ui-store";
import { cn } from "@/lib/utils";
import { buttonVariants } from "./button";
import { Image } from "./image";

const MOST_COMMON_EMOJIS = ["👍", "❤️", "😂", "🎉", "🔥"];
const FAVOURITE_EMOJIS_LIMIT = 10;

const HTCTW_CUSTOM_EMOJIS = [
	{
		id: "htctw-blue-globe",
		name: "Blue Globe",
		src: `${import.meta.env.BASE_URL}emojis/htctw/blue-globe.png`,
	},
	{
		id: "htctw-blue-globe-zoomed",
		name: "Blue Globe White Background",
		src: `${import.meta.env.BASE_URL}emojis/htctw/blue-globe-zoomed.jpg`,
	},
	{
		id: "htctw-grey-globe",
		name: "Grey Globe",
		src: `${import.meta.env.BASE_URL}emojis/htctw/grey-globe.jpg`,
	},
	{
		id: "htctw-logo-pin",
		name: "Logo Pin",
		src: `${import.meta.env.BASE_URL}emojis/htctw/logo-pin.png`,
	},
	{
		id: "htctw-design",
		name: "Design",
		src: `${import.meta.env.BASE_URL}emojis/htctw/design.png`,
	},
	{
		id: "htctw-explore",
		name: "Explore",
		src: `${import.meta.env.BASE_URL}emojis/htctw/explore.png`,
	},
	{
		id: "htctw-follow-up",
		name: "Follow-Up",
		src: `${import.meta.env.BASE_URL}emojis/htctw/follow-up.png`,
	},
	{
		id: "htctw-prepare",
		name: "Prepare",
		src: `${import.meta.env.BASE_URL}emojis/htctw/prepare.png`,
	},
	{
		id: "htctw-showcase",
		name: "Showcase",
		src: `${import.meta.env.BASE_URL}emojis/htctw/showcase.png`,
	},
];

interface EmojiPickerProps extends EmojiPickerRootProps {
	autoFocus?: boolean;
}

function SearchIcon(props: ComponentProps<"svg">) {
	return (
		<svg
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth="1.5"
			xmlns="http://www.w3.org/2000/svg"
			{...props}
		>
			<title>Search</title>
			<path d="M7 12.5a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11Zm7 1.5-3-3" />
		</svg>
	);
}

function SpinnerIcon(props: ComponentProps<"svg">) {
	return (
		<svg
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth="1.5"
			xmlns="http://www.w3.org/2000/svg"
			{...props}
		>
			<title>Spinner</title>
			<path d="M3 10a7 7 0 0 1 7-7" />
		</svg>
	);
}

function EmojiPickerRow({
	children,
	className,
	...props
}: EmojiPickerListRowProps) {
	return (
		<div
			{...props}
			className={cn(
				"scroll-my-[2vw] px-[2vw] sm:scroll-my-1.5 sm:px-1.5",
				className
			)}
		>
			{children}
		</div>
	);
}

function EmojiPickerEmoji({
	emoji,
	className,
	style,
	...props
}: EmojiPickerListEmojiProps) {
	return (
		<button
			{...props}
			className={cn(
				"relative flex aspect-square min-w-8 max-w-[calc(100%/var(--frimousse-list-columns))] flex-1 items-center justify-center overflow-hidden whitespace-nowrap rounded-[max(2vw,var(--radius-md))] text-[max(4vw,var(--text-lg))] transition-colors duration-200 ease-out data-[active]:bg-muted/80 data-[active]:duration-0 sm:size-8 sm:flex-none sm:rounded-md sm:text-lg",
				"before:-z-1 before:absolute before:inset-0 before:hidden before:items-center before:justify-center before:text-[2.5em] before:opacity-0 before:blur-lg before:saturate-200 before:transition-[display,opacity] before:transition-discrete before:duration-200 before:ease-out before:content-(--emoji) data-[active]:before:flex data-[active]:before:opacity-100 data-[active]:before:duration-0",
				className
			)}
			style={
				{
					"--emoji": `"${emoji.emoji}"`,
					...style,
				} as CSSProperties
			}
		>
			{emoji.emoji}
		</button>
	);
}

function EmojiPickerCategoryHeader({
	category,
	className,
	...props
}: EmojiPickerListCategoryHeaderProps) {
	return (
		<div
			{...props}
			className={cn(
				"after:-top-1 relative bg-popover px-4 pt-3 pb-1.5 font-medium text-secondary-foreground text-xs after:absolute after:inset-x-0 after:h-2 after:bg-popover sm:px-3",
				className
			)}
		>
			{category.label}
		</div>
	);
}

function QuickEmojiButton({
	emoji,
	onClick,
}: {
	emoji: string;
	onClick: (emoji: string) => void;
}) {
	return (
		<button
			className="relative flex size-8 items-center justify-center overflow-hidden whitespace-nowrap rounded-md text-lg transition-colors duration-200 ease-out hover:bg-muted/80"
			onClick={() => onClick(emoji)}
			type="button"
		>
			{emoji}
		</button>
	);
}

function QuickEmojiSection({
	title,
	emojis,
	onEmojiSelect,
}: {
	title: string;
	emojis: string[];
	onEmojiSelect: (emoji: string) => void;
}) {
	if (emojis.length === 0) return null;

	return (
		<div className="px-3">
			<div className="pt-3 pb-1.5 text-center font-medium text-secondary-foreground text-xs">
				{title}
			</div>
			<div className="grid grid-cols-[repeat(auto-fill,2rem)] gap-0.5">
				{emojis.map((emoji) => (
					<QuickEmojiButton emoji={emoji} key={emoji} onClick={onEmojiSelect} />
				))}
			</div>
		</div>
	);
}

function CustomEmojiButton({
	emoji,
	onClick,
}: {
	emoji: { id: string; name: string; src: string };
	onClick: (emoji: { id: string; name: string; src: string }) => void;
}) {
	return (
		<button
			className="relative flex size-8 items-center justify-center overflow-hidden rounded-md transition-colors duration-200 ease-out hover:bg-muted/80"
			onClick={() => onClick(emoji)}
			title={emoji.name}
			type="button"
		>
			<Image
				alt={emoji.name}
				className="size-6 object-contain"
				height={24}
				src={emoji.src}
				width={24}
			/>
		</button>
	);
}

function CustomEmojiSection({
	title,
	emojis,
	onEmojiSelect,
}: {
	title: string;
	emojis: { id: string; name: string; src: string }[];
	onEmojiSelect: (emoji: { id: string; name: string; src: string }) => void;
}) {
	if (emojis.length === 0) return null;

	return (
		<div className="px-3">
			<div className="pt-3 pb-1.5 text-center font-medium text-secondary-foreground text-xs">
				{title}
			</div>
			<div className="grid grid-cols-[repeat(auto-fill,2rem)] gap-0.5">
				{emojis.map((emoji) => (
					<CustomEmojiButton
						emoji={emoji}
						key={emoji.id}
						onClick={onEmojiSelect}
					/>
				))}
			</div>
		</div>
	);
}

function EmojiPicker({
	className,
	autoFocus,
	columns,
	onEmojiSelect,
	...props
}: EmojiPickerProps) {
	const trackEmojiUsage = useStore(uiStore, (state) => state.trackEmojiUsage);
	const getFavouriteEmojis = useStore(
		uiStore,
		(state) => state.getFavouriteEmojis
	);

	const favouriteEmojis = getFavouriteEmojis(FAVOURITE_EMOJIS_LIMIT);

	const handleEmojiSelect = (emoji: { emoji: string; label: string }) => {
		trackEmojiUsage(emoji.emoji);
		onEmojiSelect?.(emoji);
	};

	const handleQuickEmojiSelect = (emoji: string) => {
		trackEmojiUsage(emoji);
		onEmojiSelect?.({ emoji, label: emoji });
	};

	const handleCustomEmojiSelect = (customEmoji: {
		id: string;
		name: string;
		src: string;
	}) => {
		const emojiMarkup = `<img src="${customEmoji.src}" alt="${customEmoji.name}" class="inline-emoji" width="96" height="96" style="display:inline-block;vertical-align:middle;" />`;
		onEmojiSelect?.({ emoji: emojiMarkup, label: customEmoji.name });
	};

	const skinToneSelector = (
		<EmojiPickerPrimitive.SkinToneSelector
			className={cn(
				buttonVariants({ variant: "secondary", size: "icon" }),
				"rounded-md"
			)}
			title="Change skin tone"
		/>
	);

	return (
		<EmojiPickerPrimitive.Root
			className={cn(
				"isolate flex h-[calc(100%_-_var(--spacing)*8)] w-full flex-col sm:h-[382px]",
				className
			)}
			columns={columns}
			onEmojiSelect={handleEmojiSelect}
			{...props}
		>
			<div className="relative z-10 flex flex-none items-center gap-2 px-4 sm:px-2 sm:pt-2">
				<div className="relative flex-1">
					<SearchIcon className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-2 size-4 text-muted-foreground" />
					<EmojiPickerPrimitive.Search
						autoFocus={autoFocus}
						className="focusable h-8 w-full appearance-none rounded-md bg-muted py-1.5 pr-2.5 pl-7.5 transition duration-200 ease-out placeholder:text-muted-foreground sm:text-sm"
					/>
				</div>
				<div className="flex-none sm:hidden">{skinToneSelector}</div>
			</div>
			<div className="max-h-[30%] flex-none overflow-y-auto">
				<div className="flex items-start gap-2 px-2">
					{favouriteEmojis.length > 0 ? (
						<>
							<div className="w-[100px]">
								<QuickEmojiSection
									emojis={favouriteEmojis}
									onEmojiSelect={handleQuickEmojiSelect}
									title="Favourites"
								/>
							</div>
							<div className="-ml-2 z-10 my-2 w-px shrink-0 bg-border" />
							<div className="-ml-2 w-[100px]">
								<QuickEmojiSection
									emojis={MOST_COMMON_EMOJIS}
									onEmojiSelect={handleQuickEmojiSelect}
									title="Most common"
								/>
							</div>
							<div className="-ml-2 z-10 my-2 w-px shrink-0 bg-border" />
							<div className="-ml-2 w-[100px]">
								<CustomEmojiSection
									emojis={HTCTW_CUSTOM_EMOJIS}
									onEmojiSelect={handleCustomEmojiSelect}
									title="How to Change the World"
								/>
							</div>
						</>
					) : (
						<>
							<div className="flex-1">
								<QuickEmojiSection
									emojis={MOST_COMMON_EMOJIS}
									onEmojiSelect={handleQuickEmojiSelect}
									title="Most common"
								/>
							</div>
							<div className="z-10 my-2 w-px shrink-0 bg-border" />
							<div className="flex-1">
								<CustomEmojiSection
									emojis={HTCTW_CUSTOM_EMOJIS}
									onEmojiSelect={handleCustomEmojiSelect}
									title="How to Change the World"
								/>
							</div>
						</>
					)}
				</div>
			</div>
			<EmojiPickerPrimitive.Viewport className="scrollbar-track-[transparent] scrollbar-thumb-secondary-foreground/30 relative flex-1 outline-none">
				<EmojiPickerPrimitive.Loading className="absolute inset-0 flex items-center justify-center text-muted-foreground">
					<SpinnerIcon className="size-4 animate-spin" />
				</EmojiPickerPrimitive.Loading>
				<EmojiPickerPrimitive.Empty className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm">
					No emoji found.
				</EmojiPickerPrimitive.Empty>
				<EmojiPickerPrimitive.List
					className="select-none pb-[2vw] sm:pb-1.5"
					components={{
						Row: EmojiPickerRow,
						Emoji: EmojiPickerEmoji,
						CategoryHeader: EmojiPickerCategoryHeader,
					}}
				/>
			</EmojiPickerPrimitive.Viewport>
			<div className="z-10 hidden w-full min-w-0 max-w-(--frimousse-viewport-width) flex-none items-center gap-1 p-2 shadow-[0_-1px_--alpha(var(--color-neutral-200)/65%)] sm:flex dark:shadow-[0_-1px_var(--color-neutral-800)]">
				<EmojiPickerPrimitive.ActiveEmoji>
					{({ emoji }) =>
						emoji ? (
							<>
								<div className="flex size-8 flex-none items-center justify-center text-xl">
									{emoji.emoji}
								</div>
								<span className="truncate font-medium text-secondary-foreground text-xs">
									{emoji.label}
								</span>
							</>
						) : (
							<span className="ml-2 truncate font-medium text-muted-foreground text-xs">
								Select an emoji…
							</span>
						)
					}
				</EmojiPickerPrimitive.ActiveEmoji>
				<div className="ml-auto flex-none">{skinToneSelector}</div>
			</div>
		</EmojiPickerPrimitive.Root>
	);
}

export { EmojiPicker };
